/** Leave domain seed + localStorage persistence for the demo SPA. */

import { DEMO_EMPLOYEE } from "../lib/roles";

export const LEAVE_CHARGE_CODES = [
  { code: "PT-ACCRUED", title: "Accrued PT", labor_type: "PT" as const },
  { code: "PT-BANK", title: "Bank PT (M&A carryover)", labor_type: "PT" as const },
  { code: "PT-SICK", title: "Sick Leave (PT)", labor_type: "PT" as const },
  { code: "PT-HOLIDAY", title: "Company Holiday", labor_type: "PT" as const },
  { code: "LWOP-PERSONAL", title: "Leave Without Pay — Personal", labor_type: "LWOP" as const },
] as const;

export type LeaveRequestStatus =
  | "Submitted"
  | "With supervisor"
  | "Approved"
  | "Rejected"
  | "Cancelled";

export type LeaveRequest = {
  id: string;
  employee_id: string;
  employee_name: string;
  charge_code: string;
  hours: number;
  start_date: string;
  end_date: string;
  reason: string;
  status: LeaveRequestStatus;
  submitted_at: string;
  supervisor_id: string;
  supervisor_name: string;
  current_owner: string;
  history: { at: string; status: LeaveRequestStatus; by: string; note?: string }[];
};

export type LeaveAccrualBand = {
  id: string;
  min_years: number;
  annual_hours: number;
};

export type LeaveCarryoverPolicy = {
  id: string;
  max_hours: number;
  effective_date: string;
  end_date: string | null;
};

export type CompanyLeavePolicy = {
  company_id: string;
  /** accrual = balance chart; flex = utilization (formerly unlimited). */
  mode: "accrual" | "flex" | "unlimited";
  /** When switching to flex, charts use utilization on/after this date. */
  flex_effective_date: string | null;
  utilization_target_pct: number;
  accrual_bands: LeaveAccrualBand[];
  carryover: LeaveCarryoverPolicy[];
  pay_periods_per_year: number;
};

const STORAGE_KEY = "govsuite.demo.leaveRequests.v1";
const POLICY_KEY = "govsuite.demo.leavePolicy.v1";

export const SEED_LEAVE_POLICY: CompanyLeavePolicy = {
  company_id: "co-hfs",
  mode: "accrual",
  flex_effective_date: null,
  utilization_target_pct: 75,
  pay_periods_per_year: 26,
  accrual_bands: [
    { id: "band-1", min_years: 0, annual_hours: 120 },
    { id: "band-5", min_years: 5, annual_hours: 160 },
    { id: "band-10", min_years: 10, annual_hours: 200 },
  ],
  carryover: [
    {
      id: "co-1",
      max_hours: 40,
      effective_date: "2025-01-01",
      end_date: null,
    },
  ],
};

export const SEED_LEAVE_REQUESTS: LeaveRequest[] = [
  {
    id: "lr-seed-1",
    employee_id: DEMO_EMPLOYEE.id,
    employee_name: DEMO_EMPLOYEE.name,
    charge_code: "PT-ACCRUED",
    hours: 8,
    start_date: "2026-08-14",
    end_date: "2026-08-14",
    reason: "Personal appointment",
    status: "Approved",
    submitted_at: "2026-07-10T15:00:00.000Z",
    supervisor_id: "emp-ortiz",
    supervisor_name: "Jordan Ortiz",
    current_owner: "—",
    history: [
      {
        at: "2026-07-10T15:00:00.000Z",
        status: "Submitted",
        by: DEMO_EMPLOYEE.name,
      },
      {
        at: "2026-07-11T09:00:00.000Z",
        status: "With supervisor",
        by: "System",
      },
      {
        at: "2026-07-11T12:00:00.000Z",
        status: "Approved",
        by: "Jordan Ortiz",
        note: "Approved",
      },
    ],
  },
];

/** FY begin balances and tenure for demo employee. */
export const SEED_LEAVE_EMPLOYEE = {
  employee_id: DEMO_EMPLOYEE.id,
  employee_name: DEMO_EMPLOYEE.name,
  hire_date: "2019-03-15",
  fy_begin_accrued: 96,
  fy_pt_used: 32,
  supervisor_id: "emp-ortiz",
  supervisor_name: "Jordan Ortiz",
  /** Demo utilization YTD hours for unlimited mode. */
  billable_hours_ytd: 920,
  unbillable_hours_ytd: 180,
  available_hours_ytd: 1200,
};

export const SEED_TEAM_LEAVE: typeof SEED_LEAVE_EMPLOYEE[] = [
  SEED_LEAVE_EMPLOYEE,
  {
    employee_id: "emp-blake",
    employee_name: "Morgan Blake",
    hire_date: "2022-06-01",
    fy_begin_accrued: 80,
    fy_pt_used: 48,
    supervisor_id: "emp-ortiz",
    supervisor_name: "Jordan Ortiz",
    billable_hours_ytd: 700,
    unbillable_hours_ytd: 300,
    available_hours_ytd: 1100,
  },
];

export function perPeriodAccrual(annualHours: number, payPeriods: number): number {
  return Math.round((annualHours / payPeriods) * 100) / 100;
}

export function bandForTenure(bands: LeaveAccrualBand[], years: number): LeaveAccrualBand {
  const sorted = [...bands].sort((a, b) => a.min_years - b.min_years);
  let chosen = sorted[0];
  for (const b of sorted) {
    if (years >= b.min_years) chosen = b;
  }
  return chosen;
}

export function yearsOfService(hireDate: string, asOf: string): number {
  const h = new Date(hireDate + "T12:00:00");
  const a = new Date(asOf + "T12:00:00");
  let y = a.getFullYear() - h.getFullYear();
  const m = a.getMonth() - h.getMonth();
  if (m < 0 || (m === 0 && a.getDate() < h.getDate())) y -= 1;
  return Math.max(0, y);
}

export function loadLeaveRequests(): LeaveRequest[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as LeaveRequest[];
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch {
    /* ignore */
  }
  return SEED_LEAVE_REQUESTS.map((r) => ({
    ...r,
    history: [...r.history],
  }));
}

export function saveLeaveRequests(rows: LeaveRequest[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
}

export function loadLeavePolicy(): CompanyLeavePolicy {
  try {
    const raw = localStorage.getItem(POLICY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as CompanyLeavePolicy;
      // Migrate legacy unlimited → flex
      if (parsed.mode === "unlimited") {
        parsed.mode = "flex";
        if (!parsed.flex_effective_date) parsed.flex_effective_date = "2026-01-01";
      }
      if (parsed.flex_effective_date === undefined) parsed.flex_effective_date = null;
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return structuredClone(SEED_LEAVE_POLICY);
}

export function saveLeavePolicy(policy: CompanyLeavePolicy) {
  localStorage.setItem(POLICY_KEY, JSON.stringify(policy));
}

/** True when policy is flex/utilization as of a given date. */
export function isFlexModeAsOf(policy: CompanyLeavePolicy, asOf: string): boolean {
  if (policy.mode === "accrual") return false;
  const eff = policy.flex_effective_date;
  if (!eff) return true;
  return asOf >= eff;
}

export function roundHours(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export type CompanyFy = {
  company_id: string;
  fy_start_month: number; // 1-12
  fy_start_day: number;
};

/** Default company FY is calendar year (Jan–Dec). */
export const SEED_COMPANY_FY: CompanyFy = {
  company_id: "co-hfs",
  fy_start_month: 1,
  fy_start_day: 1,
};

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function iso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Inclusive FY window containing asOf. */
export function fyWindowContaining(fy: CompanyFy, asOf: string): { start: string; end: string } {
  const asOfD = new Date(asOf + "T12:00:00");
  let startY = asOfD.getFullYear();
  const startCandidate = new Date(startY, fy.fy_start_month - 1, fy.fy_start_day, 12);
  if (asOfD < startCandidate) startY -= 1;
  const start = new Date(startY, fy.fy_start_month - 1, fy.fy_start_day, 12);
  const end = addDays(new Date(startY + 1, fy.fy_start_month - 1, fy.fy_start_day, 12), -1);
  return { start: iso(start), end: iso(end) };
}

export type FyMonthBucket = {
  key: string;
  label: string;
  start: string;
  end: string;
  /** End-of-month leave balance (right axis). */
  balance: number;
  /** Hours used in this month segment (left axis). */
  used: number;
  /** Accrual credited in this month segment. */
  accrued: number;
  /** true when bucket end <= asOf (actual path). */
  isActual: boolean;
  /** true when bucket end > asOf (projected path). */
  isProjected: boolean;
};

/**
 * Build company-FY month segments. Mid-month FY edges create two buckets that share
 * a month name but different years (e.g. Oct '25 start stub vs Oct '26 end stub).
 */
export function buildFyMonthBuckets(
  fy: CompanyFy,
  asOf: string,
): Omit<FyMonthBucket, "balance" | "used" | "accrued" | "isActual" | "isProjected">[] {
  const { start, end } = fyWindowContaining(fy, asOf);
  const buckets: Omit<
    FyMonthBucket,
    "balance" | "used" | "accrued" | "isActual" | "isProjected"
  >[] = [];
  let cur = new Date(start + "T12:00:00");
  const endD = new Date(end + "T12:00:00");
  while (cur <= endD) {
    const y = cur.getFullYear();
    const m = cur.getMonth();
    const monthStart = new Date(y, m, 1, 12);
    const monthEnd = new Date(y, m + 1, 0, 12);
    const segStart = cur > monthStart ? cur : monthStart;
    const segEnd = endD < monthEnd ? endD : monthEnd;
    const label = `${MONTH_SHORT[m]} '${String(y).slice(2)}`;
    buckets.push({
      key: `${iso(segStart)}_${iso(segEnd)}`,
      label,
      start: iso(segStart),
      end: iso(segEnd),
    });
    cur = addDays(monthEnd, 1);
  }
  return buckets;
}

function daysInclusive(a: string, b: string): number {
  const ms = new Date(b + "T12:00:00").getTime() - new Date(a + "T12:00:00").getTime();
  return Math.max(0, Math.floor(ms / 86400000) + 1);
}

function activeLeaveHours(
  requests: LeaveRequest[],
  employeeId: string,
  segStart: string,
  segEnd: string,
  leaveType?: string,
): number {
  return requests
    .filter(
      (r) =>
        r.employee_id === employeeId &&
        (r.status === "Approved" || r.status === "Submitted" || r.status === "With supervisor") &&
        r.start_date <= segEnd &&
        (r.end_date || r.start_date) >= segStart &&
        (!leaveType || leaveType === "ALL" || r.charge_code === leaveType),
    )
    .reduce((s, r) => s + r.hours, 0);
}

/** Begin balance weight by leave type for demo (Accrued PT owns primary pool). */
function beginBalanceForType(emp: typeof SEED_LEAVE_EMPLOYEE, leaveType: string): number {
  if (leaveType === "ALL" || leaveType === "PT-ACCRUED") return emp.fy_begin_accrued;
  if (leaveType === "PT-BANK") return 24;
  if (leaveType === "PT-SICK") return 40;
  return 0;
}

function usedYtdForType(emp: typeof SEED_LEAVE_EMPLOYEE, leaveType: string): number {
  if (leaveType === "ALL" || leaveType === "PT-ACCRUED") return emp.fy_pt_used;
  if (leaveType === "PT-BANK") return 8;
  if (leaveType === "PT-SICK") return 16;
  return 0;
}

/**
 * Monthly leave path for dual-axis chart:
 * - used: hours taken in month (left axis bars)
 * - accrued: accrual credited in month
 * - balance: ending balance (right axis line)
 * Prior months = actuals; current/future = projected.
 */
export function buildFyLeaveChart(
  emp: typeof SEED_LEAVE_EMPLOYEE,
  policy: CompanyLeavePolicy,
  requests: LeaveRequest[],
  fy: CompanyFy,
  asOf = "2026-07-27",
  leaveType = "PT-ACCRUED",
): { buckets: FyMonthBucket[]; meta: ReturnType<typeof projectionPoints> } {
  const meta = projectionPoints(emp, policy, requests, asOf, leaveType);
  const raw = buildFyMonthBuckets(fy, asOf);
  const { start: fyStart, end: fyEnd } = fyWindowContaining(fy, asOf);
  const fyDays = daysInclusive(fyStart, fyEnd) || 1;
  const accrues =
    leaveType === "PT-ACCRUED" || leaveType === "ALL" || leaveType === "PT-SICK";
  const annual = accrues ? meta.annual : 0;
  const dailyAccrual = annual / fyDays;
  const daysThroughAsOf = daysInclusive(fyStart, asOf) || 1;
  const usedYtd = usedYtdForType(emp, leaveType);

  let balance = beginBalanceForType(emp, leaveType);
  const buckets: FyMonthBucket[] = raw.map((b) => {
    const segDays = daysInclusive(b.start, b.end);
    const accrual = roundHours(dailyAccrual * segDays);
    const isPast = b.end <= asOf;
    const isFuture = b.start > asOf;
    const straddles = b.start <= asOf && b.end > asOf;

    let leaveHrs = 0;
    if (isPast) {
      leaveHrs = (usedYtd * segDays) / daysThroughAsOf;
    } else if (straddles) {
      const pastInSeg = daysInclusive(b.start, asOf);
      const futureStart = iso(addDays(new Date(asOf + "T12:00:00"), 1));
      leaveHrs =
        (usedYtd * pastInSeg) / daysThroughAsOf +
        activeLeaveHours(requests, emp.employee_id, futureStart, b.end, leaveType);
    } else if (isFuture) {
      leaveHrs = activeLeaveHours(requests, emp.employee_id, b.start, b.end, leaveType);
    }
    leaveHrs = roundHours(leaveHrs);

    balance = roundHours(balance + accrual - leaveHrs);
    return {
      ...b,
      balance: roundHours(balance),
      used: leaveHrs,
      accrued: accrual,
      isActual: isPast,
      isProjected: isFuture || straddles,
    };
  });

  return { buckets, meta };
}

export function projectionPoints(
  emp: typeof SEED_LEAVE_EMPLOYEE,
  policy: CompanyLeavePolicy,
  requests: LeaveRequest[],
  asOf = "2026-07-27",
  leaveType = "PT-ACCRUED",
) {
  const years = yearsOfService(emp.hire_date, asOf);
  const band = bandForTenure(policy.accrual_bands, years);
  const perPay = perPeriodAccrual(band.annual_hours, policy.pay_periods_per_year);
  const periodsElapsed = 14; // demo midpoint of FY
  const accruedYtd = perPay * periodsElapsed;
  const future = requests
    .filter(
      (r) =>
        r.employee_id === emp.employee_id &&
        (r.status === "Approved" || r.status === "Submitted" || r.status === "With supervisor") &&
        r.start_date >= asOf &&
        (leaveType === "ALL" || r.charge_code === leaveType),
    )
    .reduce((s, r) => s + r.hours, 0);
  const begin = beginBalanceForType(emp, leaveType);
  const used = usedYtdForType(emp, leaveType);
  const mid = roundHours(begin + accruedYtd - used - future);
  const yearEndAccrual = perPay * policy.pay_periods_per_year;
  const yearEnd = roundHours(begin + yearEndAccrual - used - future);
  const carry = policy.carryover.find((c) => !c.end_date || c.end_date >= asOf);
  return {
    begin,
    accruedYtd: roundHours(accruedYtd),
    used,
    future: roundHours(future),
    mid,
    yearEnd,
    perPay,
    annual: band.annual_hours,
    carryMax: carry?.max_hours ?? null,
    years,
  };
}
